/**
 * The rules of the bindings store, on plain data (no DOM, no storage): what a valid stored
 * document looks like, who owns a key, and what a rebind may and may not do.
 *
 * Within a scheme a key belongs to at most one action; the two schemes are independent.
 */

import { ACTIONS, actionLabel, type ActionId, isActionId, PAUSE_KEY, type Scheme, SCHEMES, SLOTS } from './actions';
import { type BindingTable, defaultKeys, defaultTable } from './defaults';
import { keyLabel } from './labels';

/** What is persisted under `nightdrop.bindings.v1`. */
export interface BindingsData {
  scheme: Scheme;
  tables: Record<Scheme, BindingTable>;
}

/** Codes are short identifiers: anything else in a stored document is dropped. */
const CODE = /^[A-Za-z][A-Za-z0-9]{0,31}$/;
/** The OS owns these; a page never sees them reliably. */
const RESERVED: ReadonlySet<string> = new Set(['MetaLeft', 'MetaRight', 'OSLeft', 'OSRight', 'ContextMenu']);

export function isBindableCode(code: unknown): code is string {
  return typeof code === 'string' && CODE.test(code) && !RESERVED.has(code);
}

export function defaultData(): BindingsData {
  return { scheme: 'modern', tables: { modern: defaultTable('modern'), classic: defaultTable('classic') } };
}

/** The action a key is bound to in this table, or null. */
export function ownerOf(table: Readonly<BindingTable>, code: string): ActionId | null {
  for (const { id } of ACTIONS) if (table[id].includes(code)) return id;
  return null;
}

/** Key -> action, for the input layer's per-event lookup. */
export function lookupTable(table: Readonly<BindingTable>): Map<string, ActionId> {
  const map = new Map<string, ActionId>();
  for (const { id } of ACTIONS) for (const code of table[id]) if (!map.has(code)) map.set(code, id);
  return map;
}

/**
 * One scheme's table out of whatever was stored. Stored actions win their keys first, in list
 * order; an action the document does not mention takes whichever of its defaults are still free.
 * Pause always ends up holding Escape in slot 0.
 */
function sanitiseTable(scheme: Scheme, raw: unknown): BindingTable {
  const stored: Record<string, unknown> = typeof raw === 'object' && raw !== null ? { ...raw } : {};
  const used = new Set<string>([PAUSE_KEY]);
  const table: Partial<BindingTable> = {};
  const claim = (id: ActionId, codes: readonly unknown[]): string[] => {
    const keys = id === 'pause' ? [PAUSE_KEY] : [];
    for (const code of codes) {
      if (keys.length >= SLOTS || !isBindableCode(code) || used.has(code)) continue;
      used.add(code);
      keys.push(code);
    }
    return keys;
  };
  for (const { id } of ACTIONS) {
    const value = stored[id];
    if (Array.isArray(value)) table[id] = claim(id, value);
  }
  const full = {} as BindingTable;
  for (const { id } of ACTIONS) full[id] = table[id] ?? claim(id, defaultKeys(scheme, id));
  return full;
}

/** Turns whatever was stored into valid bindings: unknown fields dropped, clashes resolved. */
export function sanitise(raw: unknown): BindingsData {
  const doc: Record<string, unknown> = typeof raw === 'object' && raw !== null ? { ...raw } : {};
  const tables: Record<string, unknown> =
    typeof doc['tables'] === 'object' && doc['tables'] !== null ? { ...doc['tables'] } : {};
  const scheme = SCHEMES.find((s) => s === doc['scheme']) ?? 'modern';
  return {
    scheme,
    tables: {
      modern: sanitiseTable('modern', tables['modern']),
      classic: sanitiseTable('classic', tables['classic']),
    },
  };
}

/**
 * Apply one rebind to `table` in place. Returns null when it was applied, else why not - the
 * table is then untouched. `code === ''` clears the slot; clearing an empty slot is fine.
 */
export function rebindIn(table: BindingTable, actionId: string, slot: number, code: string): string | null {
  if (!isActionId(actionId)) return 'Unknown action';
  if (!Number.isInteger(slot) || slot < 0 || slot >= SLOTS) return 'No such slot';
  const keys = table[actionId];
  if (actionId === 'pause' && slot === 0) return code === PAUSE_KEY ? null : 'Esc always pauses: use the second slot';

  if (code === '') {
    if (slot < keys.length) keys.splice(slot, 1);
    return null;
  }
  if (!isBindableCode(code)) return 'That key cannot be bound';
  const owner = ownerOf(table, code);
  if (owner !== null) {
    if (owner === actionId && keys[slot] === code) return null;
    return `${keyLabel(code)} is already bound to ${actionLabel(owner)}`;
  }
  // `keys` holds only bound slots, so a slot past the end is simply the next free one.
  if (slot < keys.length) keys[slot] = code;
  else keys.push(code);
  return null;
}
